import React, { useState } from 'react';
import type { ReviewAnalysisData } from './types';

interface AnalysisSummaryProps {
  data: ReviewAnalysisData;
}

const AnalysisSummary: React.FC<AnalysisSummaryProps> = ({ data }) => {
  const [isOperationalExpanded, setIsOperationalExpanded] = useState(false);

  return (
    <div className="space-y-6">
      {/* Analysis Summary Card */}
      <div className="bg-[#212121] rounded-lg p-4 border border-[#E0E0E021]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-medium text-[#EE4423]">Analysis Summary</h2>
          <button className="text-[#EE4423] hover:text-orange-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
        </div>

        {/* Overall Sentiment */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-white mb-2">Overall Sentiment</h3>
          <span className="inline-block px-3 py-1.5 bg-[#EE4423] text-white rounded-full text-xs border border-[#4a4a4a]">
            {data.overall_sentiment}
          </span>
        </div>

        {/* Key Positive Themes */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-white mb-2">Key Positive Themes</h3>
          <div className="flex flex-wrap gap-2">
            {data.key_positive_themes.map((theme, index) => (
              <span
                key={index}
                className="px-3 py-1.5 bg-green-900/20 text-green-400 rounded-full text-xs border border-green-700/50"
              >
                {theme}
              </span>
            ))}
          </div>
        </div>

        {/* Areas for Improvement */}
        <div>
          <h3 className="text-sm font-medium text-white mb-2">Areas for Improvement</h3>
          <div className="flex flex-wrap gap-2">
            {data.key_improvement_themes.map((theme, index) => (
              <span
                key={index}
                className="px-3 py-1.5 bg-red-900/20 text-red-300 rounded-full text-xs border border-red-700/50"
              >
                {theme}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Operational Improvement Card */}
      <div className="bg-[#212121] rounded-lg p-6 border border-[#E0E0E021]">
        <div className="flex items-center justify-between mb-4 cursor-pointer" onClick={() => setIsOperationalExpanded(!isOperationalExpanded)}>
          <h2 className="text-xl font-medium text-[#EE4423]">Operational Improvement</h2>
          <button className="text-orange-500 hover:text-orange-400">
            <svg className={`w-5 h-5 transition-transform ${isOperationalExpanded ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
        </div>

        {isOperationalExpanded && (
          <div className="space-y-4">
            {/* Action Title */}
            <div>
              <h3 className="text-xs font-medium text-white mb-2">Action Required</h3>
              <p className="text-[12px] font-normal text-white bg-[#1a1a1a] p-3 rounded border border-[#4a4a4a]">
                {data.action_title}
              </p>
            </div>

            {/* Problem Identified */}
            <div>
              <h3 className="text-xs font-medium text-white mb-2">Problem Identified</h3>
              <p className="text-[12px] font-normal text-gray-300 bg-[#1a1a1a] p-3 rounded border border-[#4a4a4a] leading-relaxed">
                {data.problem_identified}
              </p>
            </div>

            {/* Suggestion */}
            <div>
              <h3 className="text-xs font-medium text-white mb-2">Suggestion</h3>
              <p className="text-[12px] font-normal text-gray-300 bg-[#1a1a1a] p-3 rounded border border-[#4a4a4a] leading-relaxed">
                {data.suggestion}
              </p>
            </div>

            {/* Rationale */}
            <div>
              <h3 className="text-xs font-medium text-white mb-2">Rationale</h3>
              <p className="text-[12px] font-normal text-gray-300 bg-[#1a1a1a] p-3 rounded border border-[#4a4a4a] leading-relaxed">
                {data.rationale}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisSummary;
